import { useContext, useEffect, useMemo, useState } from 'react'
import { followReligiousPlaceService, getPlaceDetailsService, unfollowReligiousPlaceService } from '../Service/ReligiousPlaceService'
import { createDonationCheckoutSessionService } from '../Service/DonationService'
import { useTranslation } from 'react-i18next';
import moment from 'moment';
import { PiCalendar, PiClock, PiFacebookLogoBold, PiHouse, PiImage, PiInfo, PiInstagramLogoBold, PiListBullets, PiMapPin, PiTiktokLogoBold, PiXLogoBold } from 'react-icons/pi';
import { amenityDisplayName } from '../Utils/amenityDisplayName';
import i18n from '../multilingual/config';
import { Button } from 'primereact/button';
import { Carousel } from 'primereact/carousel';
import GoogleMap from './MapComponent';
import CalendarComponent from './CalendarComponent/CalendarComponent';
import AuthContext from '../Contexts/AuthContext';
import { Dialog } from 'primereact/dialog';

/** Desktop: Google Maps in browser. iOS: Apple Maps app. Android: geo intent (Maps, Waze, etc.). */
function mapsUrlForAddress(address, lat, lng) {
    const query = encodeURIComponent(address);
    if (typeof navigator === 'undefined') {
        return `https://www.google.com/maps/search/?api=1&query=${query}`;
    }
    const ua = navigator.userAgent || '';
    const isAndroid = /Android/i.test(ua);
    const isIOS =
        /iPad|iPhone|iPod/i.test(ua) ||
        (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);

    const latN = Number(lat);
    const lngN = Number(lng);
    const hasCoords = Number.isFinite(latN) && Number.isFinite(lngN);

    if (isIOS) {
        if (hasCoords) {
            return `https://maps.apple.com/?ll=${latN},${lngN}&q=${query}`;
        }
        return `https://maps.apple.com/?q=${query}`;
    }
    if (isAndroid) {
        if (hasCoords) {
            return `geo:${latN},${lngN}`;
        }
        return `geo:0,0?q=${query}`;
    }
    return `https://www.google.com/maps/search/?api=1&query=${query}`;
}

function mapsAddressLinkProps(href) {
    if (href.startsWith('https://www.google.com/')) {
        return { target: '_blank', rel: 'noopener noreferrer' };
    }
    return { rel: 'noreferrer' };
}

function ReligiousPlaceDetailsComponent({access_key}) {

    const [religiousPlace, setReligiousPlace] = useState()
    const [loadingPlace, setLoadingPlace] = useState(() => Boolean(access_key))
    const [following, setFollowing] = useState(0)
    const { t } = useTranslation('register_place');
    const {userAuth} = useContext(AuthContext)
    const [openPictureDialog, setOpenPictureDialog] = useState(false)
    const [dialogPicture, setDialogPicture] = useState()
    const [donationAmount, setDonationAmount] = useState('10')
    const [donationType, setDonationType] = useState('one_time')
    const [donationInterval, setDonationInterval] = useState('month')
    const [donationCategory, setDonationCategory] = useState('donation')
    const [isCreatingDonation, setIsCreatingDonation] = useState(false)

    useEffect(()=>{
        handleGetPlaceDetails()
    },[access_key])

    const handleGetPlaceDetails = ()=>{
        if (!access_key) {
            setLoadingPlace(false)
            setReligiousPlace(undefined)
            return
        }
        setLoadingPlace(true)
        setReligiousPlace(undefined)
        getPlaceDetailsService(access_key, userAuth?.user?.access_key).then((res)=>{
            setLoadingPlace(false)
            if (!res || typeof res !== 'object') {
                setReligiousPlace(undefined)
                return
            }
            let amenities = res.amenities
            if (amenities == null) amenities = []
            else if (typeof amenities === 'string') {
                try {
                    amenities = JSON.parse(amenities)
                } catch {
                    amenities = []
                }
            }
            if (!Array.isArray(amenities)) amenities = []
            let ceremony_times = res.ceremony_times
            if (ceremony_times == null) ceremony_times = []
            else if (typeof ceremony_times === 'string') {
                try {
                    ceremony_times = JSON.parse(ceremony_times)
                } catch {
                    ceremony_times = []
                }
            }
            if (!Array.isArray(ceremony_times)) ceremony_times = []
            setFollowing(res.following)
            setReligiousPlace({ ...res, amenities, ceremony_times })
        }).catch(() => {
            setLoadingPlace(false)
            setReligiousPlace(undefined)
        })
    }

    const pictureTemplate = (picture) => {
        return (
            <div className="border-1 surface-border border-round m-2 text-center py-5 px-3" onClick={()=>openImage(picture.url)}>
                <img src={`https://storage.googleapis.com/religious_place_images/${picture.url}`} alt={picture.url} className="md:h-10rem h-5rem shadow-2" />
            </div>
        );
    };

    const openImage = (picture_url) => {
        setDialogPicture(picture_url)
        setOpenPictureDialog(true)
    }

    const responsiveOptions = [
        {
            breakpoint: '1400px',
            numVisible: 2,
            numScroll: 1
        },
        {
            breakpoint: '1199px',
            numVisible: 2,
            numScroll: 1
        },
        {
            breakpoint: '767px',
            numVisible: 1,
            numScroll: 1
        },
        {
            breakpoint: '575px',
            numVisible: 1,
            numScroll: 1
        }
    ];

    const handleFollow = () => {
        following ? unfollowReligiousPlaceService(following).then(res=>{
            res && setFollowing(null)
        }) : followReligiousPlaceService(userAuth?.user?.access_key,religiousPlace?.access_key).then(res=>{
            res.id && setFollowing(res.id)
        })
    }

    /** HH:mm (24h) for schedule cards */
    const formatCeremonyScheduleTime = (hhmmss) => {
        if (!hhmmss) return '';
        const pad = hhmmss.length === 5 ? `${hhmmss}:00` : hhmmss;
        const d = new Date(`1970-01-01T${pad}`);
        return d.toLocaleTimeString(i18n.language, { hour: '2-digit', minute: '2-digit', hour12: false });
    };

    const weekdayCeremonyLabel = (w) =>
        moment().day(Number(w) - 1).locale(i18n.language === 'pt' ? 'pt-br' : i18n.language === 'es' ? 'es' : 'en').format('dddd');

    const handleDonate = async () => {
        if (!religiousPlace?.access_key || !userAuth?.user?.access_key || isCreatingDonation) return
        setIsCreatingDonation(true)
        const response = await createDonationCheckoutSessionService({
            user_access_key: userAuth.user.access_key,
            religious_place_access_key: religiousPlace.access_key,
            donation_type: donationType,
            donation_category: donationCategory,
            amount: Number(donationAmount),
            currency: 'usd',
            interval: donationType === 'recurring' ? donationInterval : null
        })

        if (response?.success && response?.data?.url) {
            window.location.href = response.data.url
            return
        }
        setIsCreatingDonation(false)
        alert(t('generic_server_error_message'))
    }

    const addressMapsHref = useMemo(
        () =>
            religiousPlace?.address
                ? mapsUrlForAddress(religiousPlace.address, religiousPlace.lat, religiousPlace.lng)
                : '',
        [religiousPlace?.address, religiousPlace?.lat, religiousPlace?.lng]
    );

    const ceremoniesByWeekday = useMemo(() => {
        const map = { 1: [], 2: [], 3: [], 4: [], 5: [], 6: [], 7: [] };
        const list = religiousPlace?.ceremony_times;
        if (!Array.isArray(list)) return map;
        for (const c of list) {
            const wd = Number(c.weekday);
            if (Number.isInteger(wd) && wd >= 1 && wd <= 7) map[wd].push(c);
        }
        for (let d = 1; d <= 7; d += 1) {
            map[d].sort((a, b) => String(a.start_time).localeCompare(String(b.start_time)));
        }
        return map;
    }, [religiousPlace?.ceremony_times]);

    const todayCeremonyWeekday = new Date().getDay() + 1;

    if (loadingPlace) {
        return (
            <div className='flex justify-content-center align-items-center p-5 text-600'>
                {t('place_details_loading')}
            </div>
        )
    }

    return (
        religiousPlace ? <div className='flex flex-column gap-4'>
            <div className='flex flex-column gap-2'>
                <div className='text-center'>{religiousPlace.logo_url ? <img height={'80px'} src={`https://storage.googleapis.com/religious_place_images/${religiousPlace.logo_url}`} alt="icon" /> : <PiHouse size={'32px'} />}</div>
                <div className='text-center text-2xl font-bold'>{religiousPlace.name}</div>
                <div className='text-center'>{i18n.language === 'pt' ? religiousPlace.religion_name_pt_br : i18n.language === 'es' ? religiousPlace.religion_name_es : religiousPlace.religion_name}</div>
                <div className='text-center'><Button label={following ? t('place_details_unfollow') : t('place_details_follow')} severity={following ? 'danger' : 'success'} onClick={handleFollow} /></div>
                <div className='text-center'>{religiousPlace.comunity_size ? `${religiousPlace.comunity_size} ${t('place_details_comunity_size_text')}` : '' }</div>
            </div>
            {religiousPlace?.stripe_donations_enabled && (
            <div className='flex flex-column gap-3 p-4 border-round shadow-5 border-400'>
                <div className='w-full font-bold text-lg'>{t('place_details_donation_title')}</div>
                <div className='flex flex-column md:flex-row gap-2'>
                    <div className='flex flex-column gap-1 flex-1'>
                        <span className='font-bold'>{t('place_details_donation_amount')}</span>
                        <input
                            className='p-inputtext p-component'
                            type='number'
                            min='0.5'
                            step='0.5'
                            value={donationAmount}
                            onChange={(e)=>setDonationAmount(e.target.value)}
                        />
                    </div>
                    <div className='flex flex-column gap-1 flex-1'>
                        <span className='font-bold'>{t('place_details_donation_type')}</span>
                        <select className='p-inputtext p-component' value={donationType} onChange={(e)=>setDonationType(e.target.value)}>
                            <option value='one_time'>{t('place_details_donation_type_one_time')}</option>
                            <option value='recurring'>{t('place_details_donation_type_recurring')}</option>
                        </select>
                    </div>
                    <div className='flex flex-column gap-1 flex-1'>
                        <span className='font-bold'>{t('place_details_donation_category')}</span>
                        <select className='p-inputtext p-component' value={donationCategory} onChange={(e)=>setDonationCategory(e.target.value)}>
                            <option value='donation'>{t('place_details_donation_category_donation')}</option>
                            <option value='tithe'>{t('place_details_donation_category_tithe')}</option>
                            <option value='pledge'>{t('place_details_donation_category_pledge')}</option>
                        </select>
                    </div>
                    {donationType === 'recurring' && (
                        <div className='flex flex-column gap-1 flex-1'>
                            <span className='font-bold'>{t('place_details_donation_interval')}</span>
                            <select className='p-inputtext p-component' value={donationInterval} onChange={(e)=>setDonationInterval(e.target.value)}>
                                <option value='week'>{t('place_details_donation_interval_weekly')}</option>
                                <option value='month'>{t('place_details_donation_interval_monthly')}</option>
                                <option value='year'>{t('place_details_donation_interval_yearly')}</option>
                            </select>
                        </div>
                    )}
                </div>
                <div>
                    <Button
                        label={isCreatingDonation ? t('place_details_donation_redirecting') : t('place_details_donation_cta')}
                        onClick={handleDonate}
                        disabled={!userAuth?.user?.access_key || isCreatingDonation}
                    />
                </div>
                <div className='text-sm text-600'>
                    {t('place_details_donation_fee_message')}
                </div>
            </div>
            )}

            <Dialog className='w-min' visible={openPictureDialog} onHide={()=>setOpenPictureDialog(false)}>
                {dialogPicture && <img src={`https://storage.googleapis.com/religious_place_images/${dialogPicture}`} alt={dialogPicture} className="w-max shadow-2" />}
            </Dialog>
            <div className='flex flex-column md:flex-row gap-4'>
                <div className='flex flex-column flex-1 gap-4 p-4 border-round shadow-5 border-400'>
                    <div className='w-full font-bold text-lg flex gap-2 align-items-center'> <PiInfo /> {t('place_details_details_title')}</div>
                    {religiousPlace.ceremony_times && religiousPlace.ceremony_times.length > 0 && (
                        <div className='flex flex-column gap-3 w-full'>
                            <span className='font-bold'>{t('place_details_recurring_times_title')}</span>
                            <div
                                className='flex gap-2 overflow-x-auto pb-2 pt-1'
                                style={{ WebkitOverflowScrolling: 'touch' }}
                            >
                                {[1, 2, 3, 4, 5, 6, 7].map((wd) => {
                                    const isToday = wd === todayCeremonyWeekday;
                                    const rows = ceremoniesByWeekday[wd];
                                    const cardStyle = isToday
                                        ? {
                                              background: 'linear-gradient(165deg, #14532d 0%, #166534 45%, #15803d 100%)',
                                              color: '#f0fdf4',
                                              border: '1px solid #14532d',
                                              boxShadow: '0 4px 14px rgba(20, 83, 45, 0.35)',
                                          }
                                        : {
                                              background: '#ffffff',
                                              color: '#14532d',
                                              border: '1px solid #bbf7d0',
                                              boxShadow: '0 2px 8px rgba(21, 128, 61, 0.12)',
                                          };
                                    const accentColor = isToday ? '#86efac' : '#22c55e';
                                    return (
                                        <div
                                            key={wd}
                                            className='flex flex-column align-items-center text-center border-round p-3 flex-shrink-0'
                                            style={{ ...cardStyle, minWidth: '7.25rem', maxWidth: '10rem' }}
                                        >
                                            <div className='font-bold text-sm capitalize' style={{ lineHeight: 1.2 }}>
                                                {weekdayCeremonyLabel(wd)}
                                            </div>
                                            <div
                                                className='mt-2 mb-3'
                                                style={{
                                                    width: '1.5rem',
                                                    height: '0.2rem',
                                                    borderRadius: '99px',
                                                    background: accentColor,
                                                }}
                                            />
                                            <div className='flex flex-column gap-3 w-full align-items-center'>
                                                {rows.map((ceremony, idx) => {
                                                    const startLabel = ceremony.start_time
                                                        ? formatCeremonyScheduleTime(ceremony.start_time)
                                                        : '';
                                                    const endPart =
                                                        ceremony.finish_time &&
                                                        ` – ${formatCeremonyScheduleTime(ceremony.finish_time)}`;
                                                    return (
                                                        <div
                                                            key={`${wd}-${ceremony.start_time}-${idx}`}
                                                            className='flex flex-column gap-1 w-full'
                                                        >
                                                            <div className='text-sm font-semibold' style={{ lineHeight: 1.35 }}>
                                                                {startLabel}
                                                                {endPart || ''}
                                                            </div>
                                                            {ceremony.language_name ? (
                                                                <div
                                                                    className='text-xs'
                                                                    style={{ opacity: isToday ? 0.92 : 0.85, lineHeight: 1.3 }}
                                                                >
                                                                    ({ceremony.language_name})
                                                                </div>
                                                            ) : null}
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    )}
               
                    <div className='flex gap-2 justify-content-between'>
                        <div className='flex flex-column gap-1'>
                            <span className='font-bold'>{t('place_details_address_title')}</span>
                            {religiousPlace.address ? (
                                <a href={addressMapsHref} {...mapsAddressLinkProps(addressMapsHref)}>
                                    {religiousPlace.address}
                                </a>
                            ) : (
                                '-'
                            )}
                        </div>
                    </div>
                    <div className='flex gap-2 justify-content-between'>
                        <div className='flex flex-column gap-1'>
                            <span className='font-bold'>{t('place_details_phone_title')}</span>
                            {religiousPlace.phone_visible ? <span>{religiousPlace.phone}</span> : '-'}
                        </div>
                        <div className='flex flex-column gap-1'>
                            <span className='font-bold'>Email</span>
                            {religiousPlace.email_visible ? <span>{religiousPlace.email}</span> : '-'}
                        </div>
                    </div>
                    <div className='flex flex-column gap-1'>
                        <span className='font-bold'>{t('place_details_description_title')}</span>
                        <span>{religiousPlace.description}</span>
                    </div>
                    <div className='flex flex-column gap-1'>
                        <span className='font-bold'>{t('place_details_social_networks_title')}</span>
                        <div className='flex gap-4 justify-content-start'>
                            {religiousPlace.instagram_url && <a href={religiousPlace.instagram_url} target='_blank' rel="noreferrer"><PiInstagramLogoBold color='black' size={32} /></a>}
                            {religiousPlace.facebook_url && <a href={religiousPlace.facebook_url} target='_blank' rel="noreferrer"><PiFacebookLogoBold color='black' size={32} /></a>}
                            {religiousPlace.tiktok_url && <a href={religiousPlace.tiktok_url} target='_blank' rel="noreferrer"><PiTiktokLogoBold color='black' size={32} /></a>}
                            {religiousPlace.x_url && <a href={religiousPlace.x_url} target='_blank' rel="noreferrer"><PiXLogoBold color='black' size={32} /></a>}
                        </div>
                    </div>
                </div>
                <div className='flex flex-column flex-1 gap-2 p-4 border-round shadow-5 border-400'>
                    <div className='w-full font-bold text-lg flex gap-2 align-items-center'> <PiMapPin /> {t('place_details_localization_title')}</div>
                    <GoogleMap 
                        locations={[{
                            name:religiousPlace.name, 
                            lat: Number(religiousPlace.lat), 
                            lng: Number(religiousPlace.lng)}]} 
                        center={{lat: Number(religiousPlace.lat), lng: Number(religiousPlace.lng)}} />
                </div>
            </div>
            <div className='flex flex-column gap-2 p-4 border-round shadow-5 border-400'>
                <div className='w-full font-bold text-lg flex gap-2 align-items-center'> <PiImage /> {t('place_details_images_title')}</div>
                {religiousPlace?.pictures?.length > 0 && <Carousel item value={religiousPlace?.pictures} numVisible={2} numScroll={1} responsiveOptions={responsiveOptions} itemTemplate={pictureTemplate} />}
            </div>
            {religiousPlace?.amenities?.length > 0 ? (
            <div className='flex flex-column gap-2 p-4 border-round shadow-5 border-400'>
                <div className='w-full font-bold text-lg flex gap-2 align-items-center'> <PiListBullets /> {t('place_details_amenities_title')}</div>
                <div className='flex flex-wrap gap-2'>
                    {religiousPlace.amenities.map((a) => (
                        <span key={a.id} className='px-3 py-1 border-1 surface-border border-round'>{amenityDisplayName(a, i18n.language)}</span>
                    ))}
                </div>
            </div>
            ) : null}
            <div className='flex flex-column gap-2 p-4 border-round shadow-5 border-400'>
                    <div className='w-full font-bold text-lg flex gap-2 align-items-center'> <PiCalendar /> {t('place_details_events_title')}</div>
                    <div>
                        <CalendarComponent place_access_key={religiousPlace.access_key} ceremony_times={religiousPlace.ceremony_times} />
                    </div>
            </div>
            
            
        </div> : t('place_details_not_found')
    )
}

export default ReligiousPlaceDetailsComponent